// src/components/sensors/SensorDetailsSections.js
// FlowSense — memoised sections of the sensor details drawer — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// Each section only re-renders when its own props change, so a live reading or
// the 5 s clock doesn't redraw the whole drawer. No infinite animations.
//   LiveHero · RecentReadingsCard · FlowSenseAiCard · AlertsAndWorkOrders ·
//   LinkedAssetCard · DiagnosticsCard · TopologyCard · RawPayload
// ═══════════════════════════════════════════════════════════════════════════
import React, { memo, useMemo, useState } from 'react';
import {
  Badge, Box, Button, CircularProgress, CircularProgressLabel, Code, Collapse, Flex, HStack, Icon,
  IconButton, Progress, SimpleGrid, Stat, StatHelpText, StatLabel, StatNumber, Text, Tooltip, VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAccessTime, MdAssignment, MdAutoAwesome, MdBatteryAlert, MdBatteryChargingFull, MdCheckCircle, MdCloudDone,
  MdCode, MdContentCopy, MdErrorOutline, MdExpandLess, MdExpandMore, MdHistory, MdInfo, MdLightbulbOutline,
  MdLink, MdLocationOn, MdMemory, MdMyLocation, MdNotificationsActive, MdOpenInNew, MdSignalWifi4Bar,
  MdSignalWifiOff, MdTimer, MdTrendingDown, MdTrendingFlat, MdTrendingUp, MdWarning, MdWaterDrop,
} from 'react-icons/md';
import { LIVE_STATE_META, formatAgo, formatDurationShort } from './sensorLiveStatus';
import { AI_LEVEL_META } from './sensorAiInsights';
import { EXPECTED_CADENCE_SEC, INSIGHT_META, LEVELS, PSI_HIGH, PSI_LOW } from './sensorDetailsInsights';
import { LiveStatusIndicator } from './SensorLiveUi';

const PSI_GAUGE_MAX = 150;
const LEVEL_FILL = { EMPTY: 5, LOW: 30, MEDIUM: 60, HIGH: 100 };
const hasValue = (v) => v !== null && v !== undefined && v !== '';
// DATE columns (due_date) arrive as "YYYY-MM-DD" or midnight-UTC ISO — show the calendar date as stored,
// not shifted a day back by the browser's UTC-5 offset
const fmtDateOnly = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString() : new Date(v).toLocaleDateString();
};

export const SectionTitle = ({ children, right }) => {
  const color = useColorModeValue('gray.500', 'gray.400');
  return (
    <Flex justify="space-between" align="center" mb={3}>
      <Text fontSize="xs" fontWeight="800" color={color} textTransform="uppercase" letterSpacing="0.06em">{children}</Text>
      {right}
    </Flex>
  );
};

/** Responsive SVG line / step chart (no chart library — cheap to redraw on every reading). */
export const MiniSeriesChart = memo(({ points, kind, height = 96, color }) => {
  const grid = useColorModeValue('#E2E8F0', '#2D3748');
  const g = useMemo(() => {
    if (!points || points.length < 2) return null;
    const W = 320; const H = height; const pad = 6;
    const t0 = points[0].t; const t1 = points[points.length - 1].t; const span = Math.max(1, t1 - t0);
    let lo; let hi;
    if (kind === 'water_level') { lo = 0; hi = 3; } else {
      lo = Math.min(...points.map((p) => p.value)); hi = Math.max(...points.map((p) => p.value));
      const padV = Math.max(1, (hi - lo) * 0.15); lo -= padV; hi += padV;
    }
    const x = (t) => pad + ((t - t0) / span) * (W - pad * 2);
    const y = (v) => pad + (1 - (v - lo) / (hi - lo || 1)) * (H - pad * 2);
    let d = '';
    points.forEach((p, i) => {
      const px = x(p.t).toFixed(1); const py = y(p.value).toFixed(1);
      if (!i) d = `M${px},${py}`;
      else d += kind === 'water_level' ? ` H${px} V${py}` : ` L${px},${py}`;
    });
    const area = kind === 'water_level' ? null : `${d} L${x(t1).toFixed(1)},${H - pad} L${x(t0).toFixed(1)},${H - pad} Z`;
    const lines = kind === 'water_level'
      ? [0, 1, 2, 3].map((v) => ({ y: y(v), label: LEVELS[v] }))
      : [PSI_LOW, PSI_HIGH].filter((v) => v > lo && v < hi).map((v) => ({ y: y(v), label: `${v}`, warn: true }));
    return { W, H, d, area, lines };
  }, [points, kind, height]);
  if (!g) return null;
  const stroke = color || (kind === 'water_level' ? '#00B5D8' : '#3182CE');
  return (
    <svg width="100%" viewBox={`0 0 ${g.W} ${g.H}`} preserveAspectRatio="none" role="img" aria-label="Recent readings chart" style={{ display: 'block' }}>
      {g.lines.map((l) => (
        <g key={l.label}>
          <line x1={0} x2={g.W} y1={l.y} y2={l.y} stroke={l.warn ? '#DD6B20' : grid} strokeDasharray="3 3" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          <text x={g.W - 2} y={l.y - 2} textAnchor="end" fontSize="8" fill="#718096">{l.label}</text>
        </g>
      ))}
      {g.area && <path d={g.area} fill={stroke} fillOpacity={0.12} stroke="none" />}
      <path d={g.d} fill="none" stroke={stroke} strokeWidth={2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
});
MiniSeriesChart.displayName = 'MiniSeriesChart';

// ───────────────────────────────────────────────────────────────────────────
export const LiveHero = memo(({ kind, rawPsi, rawLevel, row, thresholds, ageLabel, measuredMs, delta }) => {
  const isWater = kind === 'water_level';
  const heroGradient = useColorModeValue(isWater ? 'linear(to-br, white, cyan.50)' : 'linear(to-br, white, blue.50)', isWater ? 'linear(to-br, gray.800, cyan.900)' : 'linear(to-br, gray.800, blue.900)');
  const tankBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const sub = useColorModeValue('gray.500', 'gray.400');
  const border = useColorModeValue('gray.200', 'gray.700');
  const meta = LIVE_STATE_META[row.liveState] || LIVE_STATE_META.no_data;
  const online = meta.isOnline;
  const gauge = !online || rawPsi === null ? 'gray' : rawPsi > PSI_HIGH ? 'red' : rawPsi < PSI_LOW ? 'orange' : 'green';
  const fill = rawLevel ? LEVEL_FILL[rawLevel] ?? 0 : 0;
  const levelScheme = rawLevel === 'HIGH' || rawLevel === 'EMPTY' ? 'red' : rawLevel === 'MEDIUM' ? 'blue' : rawLevel === 'LOW' ? 'orange' : 'gray';
  const valueText = isWater ? rawLevel || 'NO READING' : rawPsi !== null ? `${rawPsi.toFixed(1)} PSI` : '— PSI';
  return (
    <Box p={4} borderRadius="2xl" borderWidth="1.5px" borderColor={online ? (isWater ? 'cyan.300' : 'blue.300') : border} bgGradient={heroGradient} boxShadow="sm">
      <Flex justify="space-between" align="center" gap={3}>
        <VStack align="start" spacing={1.5}>
          <HStack spacing={2}>
            <LiveStatusIndicator row={row} thresholds={thresholds} ageLabel={ageLabel} />
            <Text fontSize="xs" fontWeight="700" color={sub}>{meta.label}</Text>
          </HStack>
          <HStack spacing={3} fontSize="2xs" color={sub} flexWrap="wrap">
            {ageLabel && (
              <Tooltip hasArrow label={`Last data received ${row.lastHeardMs ? new Date(row.lastHeardMs).toLocaleString() : '—'}`}>
                <HStack spacing={1}><Icon as={MdAccessTime} boxSize={3} /><Text fontWeight="700">{ageLabel}</Text></HStack>
              </Tooltip>
            )}
            {measuredMs && <Text>measured {new Date(measuredMs).toLocaleTimeString()}</Text>}
            {delta !== null && !isWater && (
              <HStack spacing={0.5} color={Math.abs(delta) < 0.05 ? sub : delta > 0 ? 'red.400' : 'blue.400'}>
                <Icon as={Math.abs(delta) < 0.05 ? MdTrendingFlat : delta > 0 ? MdTrendingUp : MdTrendingDown} boxSize={3} />
                <Text fontWeight="700">{delta > 0 ? '+' : ''}{delta.toFixed(1)} PSI</Text>
              </HStack>
            )}
          </HStack>
        </VStack>
        <HStack spacing={4} align="center">
          {!isWater && rawPsi !== null && (
            <Tooltip label={`Scale 0–${PSI_GAUGE_MAX} PSI · normal ${PSI_LOW}–${PSI_HIGH}`} hasArrow>
              <Box>
                <CircularProgress value={Math.min(100, Math.max(0, (rawPsi / PSI_GAUGE_MAX) * 100))} color={`${gauge}.400`} size="64px" thickness="10px" aria-label={valueText}>
                  <CircularProgressLabel fontSize="10px" fontWeight="800">{gauge === 'red' ? 'HIGH' : gauge === 'orange' ? 'LOW' : gauge === 'green' ? 'OK' : '—'}</CircularProgressLabel>
                </CircularProgress>
              </Box>
            </Tooltip>
          )}
          {isWater && (
            <Box w="28px" h="64px" borderRadius="md" borderWidth="2px" borderColor={`${levelScheme}.300`} bg={tankBg} position="relative" overflow="hidden" aria-label={`Tank fill ${fill}%`}>
              <Box position="absolute" bottom={0} left={0} right={0} bg={`${levelScheme}.400`} h={`${fill}%`} transition="height 0.6s ease-out" />
            </Box>
          )}
          <Text fontSize="2xl" fontWeight="900" whiteSpace="nowrap" color={online ? (isWater ? 'cyan.500' : 'blue.500') : 'gray.400'} data-testid="sensor-current-value">
            {valueText}
          </Text>
        </HStack>
      </Flex>
    </Box>
  );
});
LiveHero.displayName = 'LiveHero';

// ───────────────────────────────────────────────────────────────────────────
export const RecentReadingsCard = memo(({ kind, points, stats, onViewHistory, isLoading }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const sub = useColorModeValue('gray.500', 'gray.400');
  const isWater = kind === 'water_level';
  const tiles = isWater
    ? [
      ...LEVELS.slice().reverse().map((l) => ({ label: l, value: stats.counts ? stats.counts[l] : 0 })),
      { label: 'Changes', value: stats.transitions ?? 0 },
    ]
    : [
      { label: 'Min', value: stats.min !== null && stats.min !== undefined ? stats.min.toFixed(1) : '—' },
      { label: 'Avg', value: stats.avg !== null && stats.avg !== undefined ? stats.avg.toFixed(1) : '—' },
      { label: 'Max', value: stats.max !== null && stats.max !== undefined ? stats.max.toFixed(1) : '—' },
      { label: 'σ', value: stats.stdev !== null && stats.stdev !== undefined ? stats.stdev.toFixed(2) : '—' },
    ];
  return (
    <Box p={4} bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={border}>
      <SectionTitle right={onViewHistory && (
        <Button size="xs" variant="ghost" leftIcon={<MdHistory />} onClick={onViewHistory}>Full history</Button>
      )}>
        Recent readings {stats.n ? `· ${stats.n}` : ''}
      </SectionTitle>
      {points.length > 1 ? (
        <>
          <MiniSeriesChart points={points} kind={kind} />
          <Flex justify="space-between" fontSize="2xs" color={sub} mt={1}>
            <Text>{new Date(points[0].t).toLocaleTimeString()}</Text>
            <Text>{new Date(points[points.length - 1].t).toLocaleTimeString()}</Text>
          </Flex>
          <SimpleGrid columns={tiles.length} spacing={2} mt={3}>
            {tiles.map((t) => (
              <Box key={t.label} textAlign="center">
                <Text fontSize="2xs" color={sub} fontWeight="700">{t.label}{!isWater && t.label !== 'σ' ? '' : ''}</Text>
                <Text fontSize="sm" fontWeight="800">{t.value}</Text>
              </Box>
            ))}
          </SimpleGrid>
          <HStack spacing={3} mt={2} fontSize="2xs" color={sub} flexWrap="wrap">
            {stats.medianIntervalSec !== null && (
              <HStack spacing={1}><Icon as={MdTimer} boxSize={3} /><Text>every ~{Math.round(stats.medianIntervalSec)} s (expected {EXPECTED_CADENCE_SEC} s)</Text></HStack>
            )}
            {stats.maxGapSec !== null && stats.gaps > 0 && <Text>longest gap {formatDurationShort(stats.maxGapSec * 1000)}</Text>}
            {!isWater && <Text>PSI · normal {PSI_LOW}–{PSI_HIGH}</Text>}
          </HStack>
        </>
      ) : (
        <Text fontSize="xs" color={sub}>{isLoading ? 'Loading readings…' : points.length === 1 ? 'Only one reading so far — the chart starts with the next one.' : 'No readings stored for this sensor yet.'}</Text>
      )}
    </Box>
  );
});
RecentReadingsCard.displayName = 'RecentReadingsCard';

// ───────────────────────────────────────────────────────────────────────────
const MiniSpark = ({ values, color = '#805AD5', width = 110, height = 26 }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values); const max = Math.max(...values); const range = max - min || 1;
  const step = (width - 4) / (values.length - 1);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${(2 + i * step).toFixed(1)},${(height - 2 - ((v - min) / range) * (height - 4)).toFixed(1)}`).join(' ');
  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}><path d={d} fill="none" stroke={color} strokeWidth={2} /></svg>;
};

export const FlowSenseAiCard = memo(({ kind, ai, meta, insights, now }) => {
  const border = useColorModeValue('purple.200', 'purple.800');
  const bg = useColorModeValue('rgba(159, 122, 234, 0.05)', 'rgba(159, 122, 234, 0.12)');
  const tileBg = useColorModeValue('rgba(255, 255, 255, 0.75)', 'rgba(26, 32, 44, 0.7)');
  const tileBorder = useColorModeValue('gray.200', 'gray.700');
  const sub = useColorModeValue('gray.500', 'gray.400');
  const [showAll, setShowAll] = useState(false);
  const levelMeta = AI_LEVEL_META[ai.level] || AI_LEVEL_META.none;
  const shown = showAll ? insights : insights.slice(0, 5);
  const floodPct = ai.flood ? Math.round(ai.flood.probability * 100) : null;
  const eta = ai.flood?.minutesToOvertop;
  const TrendIcon = meta.trend === 'rising' ? MdTrendingUp : meta.trend === 'falling' ? MdTrendingDown : MdTrendingFlat;
  const headline = insights.find((i) => i.level !== 'ok') || insights[0];

  return (
    <Box p={4} borderRadius="2xl" borderWidth="1.5px" borderColor={border} bg={bg} data-testid="flowsense-ai-card">
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={1.5}>
          <Icon as={MdAutoAwesome} color="purple.400" boxSize={4} />
          <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em">FlowSense AI</Text>
        </HStack>
        <HStack spacing={1}>
          {meta.source && (
            <Tooltip hasArrow label={meta.source === 'edge' ? 'In-browser edge model (no fresh server prediction)' : 'FlowSense ML service'}>
              <Badge colorScheme={meta.source === 'edge' ? 'cyan' : 'purple'} variant="subtle" fontSize="2xs" borderRadius="full" display="flex" alignItems="center" gap={1}>
                <Icon as={meta.source === 'edge' ? MdMemory : MdCloudDone} boxSize={3} />{meta.source.toUpperCase()}{meta.stale ? ' · STALE' : ''}
              </Badge>
            </Tooltip>
          )}
          <Badge colorScheme={levelMeta.color} variant="solid" fontSize="2xs" borderRadius="full" px={2}>{levelMeta.label}</Badge>
        </HStack>
      </Flex>

      {headline && (
        <Text fontSize="sm" fontWeight="700" mb={3} data-testid="ai-headline">{headline.title}</Text>
      )}

      {/* Model tiles — only real values */}
      <SimpleGrid columns={3} spacing={2} mb={3}>
        <Box p={2.5} bg={tileBg} borderRadius="xl" textAlign="center" borderWidth="1px" borderColor={tileBorder}>
          <Text fontSize="2xs" color={sub} fontWeight="700">{kind === 'water_level' ? 'Flood risk' : 'Flood model'}</Text>
          <Text fontSize="lg" fontWeight="900" color={floodPct !== null && floodPct >= 65 ? 'red.500' : 'purple.500'}>{floodPct !== null ? `${floodPct}%` : '—'}</Text>
          {floodPct !== null && <Progress value={floodPct} size="xs" colorScheme={floodPct >= 65 ? 'red' : floodPct >= 35 ? 'orange' : 'green'} borderRadius="full" mt={1} />}
          {floodPct === null && <Text fontSize="2xs" color={sub}>{kind === 'water_level' ? 'no prediction' : 'water sensors only'}</Text>}
        </Box>
        <Box p={2.5} bg={tileBg} borderRadius="xl" textAlign="center" borderWidth="1px" borderColor={tileBorder}>
          <Text fontSize="2xs" color={sub} fontWeight="700">Spill ETA</Text>
          <Text fontSize="sm" fontWeight="800" mt={0.5}>{eta !== null && eta !== undefined ? (eta <= 0 ? 'NOW' : `~${Math.round(eta)} min`) : '—'}</Text>
          {eta > 0 && <Text fontSize="2xs" color={sub}>≈ {new Date(now + eta * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>}
        </Box>
        <Box p={2.5} bg={tileBg} borderRadius="xl" textAlign="center" borderWidth="1px" borderColor={tileBorder}>
          <Text fontSize="2xs" color={sub} fontWeight="700">Asset health · RUL</Text>
          <Text fontSize="sm" fontWeight="800" color={ai.health?.level === 'critical' ? 'red.500' : ai.health?.level === 'warning' ? 'orange.500' : 'teal.500'} mt={0.5}>
            {ai.health?.score !== null && ai.health?.score !== undefined ? `${Math.round(ai.health.score)}` : '—'}
            <Text as="span" fontSize="2xs" color={sub}> · {ai.health?.rulDays !== null && ai.health?.rulDays !== undefined ? `${Math.round(ai.health.rulDays)} d` : '—'}</Text>
          </Text>
          {ai.health?.status && <Text fontSize="2xs" color={sub} noOfLines={1}>{ai.health.status}</Text>}
        </Box>
      </SimpleGrid>

      {ai.anomaly && (
        <Box mb={3} p={2.5} borderRadius="lg" borderWidth="1px" borderColor={ai.anomaly.severity === 'critical' ? 'red.300' : 'orange.300'} bg={tileBg}>
          <HStack spacing={1.5}>
            <Icon as={MdWarning} color={ai.anomaly.severity === 'critical' ? 'red.400' : 'orange.400'} />
            <Text fontSize="xs" fontWeight="800">{ai.anomaly.label}</Text>
          </HStack>
          <Text fontSize="xs" mt={0.5}>{ai.anomaly.detail}</Text>
          <Text fontSize="2xs" color={sub}>{ai.anomaly.method}</Text>
        </Box>
      )}

      {/* Insights */}
      <Text fontSize="2xs" fontWeight="800" color={sub} textTransform="uppercase" mb={1.5}>Insights</Text>
      <VStack align="stretch" spacing={1.5} data-testid="ai-insights">
        {shown.map((i) => {
          const m = INSIGHT_META[i.level];
          return (
            <HStack key={`${i.source}-${i.title}`} align="start" spacing={2}>
              <Icon as={i.level === 'ok' ? MdCheckCircle : i.level === 'info' ? MdInfo : i.level === 'critical' ? MdErrorOutline : MdWarning} color={`${m.color}.400`} boxSize={4} mt="1px" />
              <Box flex="1">
                <Text fontSize="xs" fontWeight="700">{i.title}</Text>
                {i.detail && <Text fontSize="2xs" color={sub}>{i.detail}</Text>}
              </Box>
              <Text fontSize="2xs" color={sub} whiteSpace="nowrap">{i.source}</Text>
            </HStack>
          );
        })}
      </VStack>
      {insights.length > 5 && (
        <Button size="xs" variant="link" mt={2} onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show fewer' : `Show all ${insights.length}`}</Button>
      )}

      {/* Model metadata */}
      <HStack spacing={4} fontSize="2xs" color={sub} flexWrap="wrap" mt={3}>
        {meta.confidence !== null && (
          <HStack spacing={1}><CircularProgress value={meta.confidence} size="14px" thickness="14px" color="purple.400" /><Text fontWeight="700">{meta.confidence}% confidence</Text></HStack>
        )}
        {meta.trend && (
          <HStack spacing={0.5} color={meta.trend === 'rising' ? 'red.400' : meta.trend === 'falling' ? 'green.400' : sub}>
            <Icon as={TrendIcon} boxSize={3.5} /><Text fontWeight="700">Risk {meta.trend}</Text>
          </HStack>
        )}
        {meta.updatedAt && <Text>Updated {formatAgo(new Date(meta.updatedAt).getTime(), now)}</Text>}
        {meta.modelVersion && <Text>Model {meta.modelVersion}</Text>}
      </HStack>
      {meta.forecast.length > 1 && (
        <Flex justify="space-between" align="center" mt={2} pt={2} borderTopWidth="1px" borderColor={tileBorder}>
          <Text fontSize="2xs" color={sub}>Risk forecast</Text>
          <MiniSpark values={meta.forecast} />
        </Flex>
      )}
      {meta.factors.length > 0 && (
        <Box mt={3} pt={2} borderTopWidth="1px" borderColor={tileBorder}>
          <Text fontSize="2xs" fontWeight="800" color={sub} textTransform="uppercase" mb={1.5}>Top drivers</Text>
          {meta.factors.map((f) => (
            <HStack key={f.label} justify="space-between" fontSize="xs"><Text>{f.label}</Text>{f.weight !== null && <Text color={sub} fontFamily="mono">{f.weight.toFixed(2)}</Text>}</HStack>
          ))}
        </Box>
      )}
      {meta.recommendations.length > 0 && (
        <Box mt={3} p={2.5} borderRadius="lg" bg={tileBg} borderWidth="1px" borderColor={tileBorder}>
          <HStack spacing={1} mb={1}><Icon as={MdLightbulbOutline} color="purple.400" boxSize={3.5} /><Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={sub}>Recommended</Text></HStack>
          {meta.recommendations.map((r) => <Text key={r} fontSize="xs">• {r}</Text>)}
        </Box>
      )}
    </Box>
  );
});
FlowSenseAiCard.displayName = 'FlowSenseAiCard';

// ───────────────────────────────────────────────────────────────────────────
const SEV_COLOR = { critical: 'red', high: 'orange', medium: 'yellow', low: 'gray' };
const PRIO_COLOR = { critical: 'red', high: 'orange', medium: 'blue', low: 'gray' };

export const AlertsAndWorkOrders = memo(({ alerts, workOrders, assetName, onOpenWorkOrder, now, isLoading }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const sub = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box>
      <SectionTitle>Alerts & work orders</SectionTitle>
      <VStack align="stretch" spacing={2}>
        {alerts.length === 0 && workOrders.length === 0 && (
          <HStack p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border} spacing={2}>
            <Icon as={MdCheckCircle} color="green.400" />
            <Text fontSize="xs" color={sub}>{isLoading ? 'Loading…' : 'No open alerts or work orders.'}</Text>
          </HStack>
        )}
        {alerts.map((a) => (
          <HStack key={`a-${a.id}`} p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border} align="start" spacing={2.5} data-testid="sensor-alert">
            <Icon as={MdNotificationsActive} color={`${SEV_COLOR[a.severity] || 'gray'}.400`} boxSize={4} mt="2px" />
            <Box flex="1">
              <Text fontSize="xs" fontWeight="700">{a.alert_message}</Text>
              <HStack spacing={2} fontSize="2xs" color={sub} flexWrap="wrap">
                <Badge colorScheme={SEV_COLOR[a.severity] || 'gray'} fontSize="2xs">{a.severity}</Badge>
                <Text>{String(a.status).replace(/_/g, ' ')}</Text>
                {a.triggered_at && <Text>{formatAgo(new Date(a.triggered_at).getTime(), now)}</Text>}
                {hasValue(a.last_violation_value) && <Text>value {a.last_violation_value}</Text>}
              </HStack>
            </Box>
            {a.work_order_id && onOpenWorkOrder && (
              <Button size="xs" variant="ghost" onClick={() => onOpenWorkOrder(a.work_order_id)}>WO {a.work_order_id}</Button>
            )}
          </HStack>
        ))}
        {workOrders.map((w) => (
          <HStack key={`w-${w.id}`} p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border} align="start" spacing={2.5} data-testid="sensor-work-order">
            <Icon as={MdAssignment} color="purple.400" boxSize={4} mt="2px" />
            <Box flex="1">
              <Text fontSize="xs" fontWeight="700">{w.title}</Text>
              <HStack spacing={2} fontSize="2xs" color={sub} flexWrap="wrap">
                <Badge colorScheme={PRIO_COLOR[w.priority] || 'gray'} fontSize="2xs">{w.priority}</Badge>
                <Text>{String(w.status).replace(/_/g, ' ')}</Text>
                {assetName && <Text>{assetName}</Text>}
                {w.due_date && <Text>due {fmtDateOnly(w.due_date)}</Text>}
              </HStack>
            </Box>
            {onOpenWorkOrder && <Button size="xs" variant="ghost" rightIcon={<MdOpenInNew />} onClick={() => onOpenWorkOrder(w.id)}>Open</Button>}
          </HStack>
        ))}
      </VStack>
    </Box>
  );
});
AlertsAndWorkOrders.displayName = 'AlertsAndWorkOrders';

// ───────────────────────────────────────────────────────────────────────────
export const LinkedAssetCard = memo(({ asset, linkedType, linkedId, onSelectEntity }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const sub = useColorModeValue('gray.500', 'gray.400');
  if (!asset && !linkedId) return null;
  return (
    <HStack p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border} justify="space-between" data-testid="linked-asset">
      <HStack spacing={2.5}>
        <Icon as={MdLink} color="purple.400" boxSize={4} />
        <Box>
          <Text fontSize="2xs" color={sub} fontWeight="700">{String(asset?.type || linkedType || 'Linked entity').toUpperCase()}</Text>
          <Text fontSize="xs" fontWeight="800">{asset?.name || `#${linkedId}`}</Text>
          {asset && (
            <HStack spacing={2} fontSize="2xs" color={sub}>
              <Text>#{asset.id}</Text>
              {asset.status && <Badge fontSize="2xs" colorScheme={asset.status === 'operational' ? 'green' : asset.status === 'unknown' ? 'gray' : 'orange'}>{String(asset.status).replace(/_/g, ' ')}</Badge>}
              {asset.location_description && <Text noOfLines={1}>{asset.location_description}</Text>}
            </HStack>
          )}
        </Box>
      </HStack>
      {onSelectEntity && (
        <Button size="xs" variant="ghost" colorScheme="purple" onClick={() => onSelectEntity(linkedType || 'asset', asset || { id: linkedId })}>Inspect</Button>
      )}
    </HStack>
  );
});
LinkedAssetCard.displayName = 'LinkedAssetCard';

// ───────────────────────────────────────────────────────────────────────────
const signalMeta = (q) => (q === null ? { color: 'gray', label: 'Not reported' } : q >= 70 ? { color: 'green', label: 'Strong' } : q >= 40 ? { color: 'orange', label: 'Fair' } : { color: 'red', label: 'Weak' });
const batteryMeta = (b) => (b === null ? { color: 'gray', label: 'Not reported' } : b >= 50 ? { color: 'green', label: 'Healthy' } : b >= 20 ? { color: 'orange', label: 'Replace soon' } : { color: 'red', label: 'Critical' });

export const DiagnosticsCard = memo(({ signalQuality, batteryLevel, specs, offlineNote }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const sub = useColorModeValue('gray.500', 'gray.400');
  const s = signalMeta(signalQuality);
  const b = batteryMeta(batteryLevel);
  return (
    <Box>
      <SectionTitle>Device diagnostics</SectionTitle>
      <SimpleGrid columns={2} spacing={3}>
        <Stat p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border}>
          <StatLabel fontSize="xs" display="flex" alignItems="center" gap={1}>
            <Icon as={signalQuality === null ? MdSignalWifiOff : MdSignalWifi4Bar} color={`${s.color}.400`} /> Signal quality
          </StatLabel>
          <StatNumber fontSize="xl" color={signalQuality === null ? sub : undefined}>{signalQuality !== null ? `${signalQuality}%` : '—'}</StatNumber>
          {signalQuality !== null && <Progress value={signalQuality} size="xs" colorScheme={s.color} borderRadius="full" my={1} />}
          <StatHelpText mb={0} fontSize="2xs">{s.label}</StatHelpText>
        </Stat>
        <Stat p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border}>
          <StatLabel fontSize="xs" display="flex" alignItems="center" gap={1}>
            <Icon as={batteryLevel !== null && batteryLevel < 20 ? MdBatteryAlert : MdBatteryChargingFull} color={`${b.color}.400`} /> Battery / supply
          </StatLabel>
          <StatNumber fontSize="xl" color={batteryLevel === null ? sub : undefined}>{batteryLevel !== null ? `${batteryLevel}%` : '—'}</StatNumber>
          {batteryLevel !== null && <Progress value={batteryLevel} size="xs" colorScheme={b.color} borderRadius="full" my={1} />}
          <StatHelpText mb={0} fontSize="2xs">{b.label}</StatHelpText>
        </Stat>
      </SimpleGrid>
      {specs.length > 0 && (
        <SimpleGrid columns={2} spacing={2} mt={3} data-testid="sensor-specs">
          {specs.map((x) => (
            <Box key={x.label} px={3} py={2} borderRadius="lg" borderWidth="1px" borderColor={border}>
              <Text fontSize="2xs" color={sub} fontWeight="700" textTransform="uppercase">{x.label}</Text>
              <Tooltip label={String(x.value)} hasArrow openDelay={400}><Text fontSize="xs" fontWeight="700" noOfLines={1}>{x.value}</Text></Tooltip>
            </Box>
          ))}
        </SimpleGrid>
      )}
      {offlineNote && (
        <HStack mt={3} p={2.5} borderRadius="lg" bg="rgba(229, 62, 62, 0.08)" spacing={2}>
          <Icon as={MdWarning} color="red.400" />
          <Text fontSize="xs">{offlineNote}</Text>
        </HStack>
      )}
    </Box>
  );
});
DiagnosticsCard.displayName = 'DiagnosticsCard';

// ───────────────────────────────────────────────────────────────────────────
export const TopologyCard = memo(({ kind, typeLabel, lat, lng, onCopy, onLocate }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const sub = useColorModeValue('gray.500', 'gray.400');
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  return (
    <VStack align="stretch" spacing={2.5}>
      <HStack p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border} justify="space-between">
        <HStack spacing={2.5}>
          <Icon as={kind === 'water_level' ? MdWaterDrop : MdInfo} color="gray.400" boxSize={4} />
          <Box>
            <Text fontSize="2xs" color={sub} fontWeight="700">CLASSIFICATION</Text>
            <Text fontSize="xs" fontWeight="800" textTransform="uppercase">{typeLabel}</Text>
          </Box>
        </HStack>
        <Badge colorScheme={kind === 'water_level' ? 'cyan' : 'blue'}>{kind === 'water_level' ? 'water level' : 'pressure'}</Badge>
      </HStack>
      <HStack p={3} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border} justify="space-between">
        <HStack spacing={2.5}>
          <Icon as={MdLocationOn} color="red.400" boxSize={4} />
          <Box>
            <Text fontSize="2xs" color={sub} fontWeight="700">COORDINATES</Text>
            <Text fontSize="xs" fontWeight="800">{hasCoords ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : 'Not placed on the map'}</Text>
          </Box>
        </HStack>
        {hasCoords && (
          <HStack spacing={0.5}>
            <Tooltip label="Copy coordinates" hasArrow>
              <IconButton size="xs" variant="ghost" icon={<MdContentCopy />} onClick={() => onCopy(`${lat.toFixed(6)}, ${lng.toFixed(6)}`, 'Coordinates')} aria-label="Copy coordinates" />
            </Tooltip>
            {onLocate && (
              <Tooltip label="Locate on map" hasArrow>
                <IconButton size="xs" variant="ghost" colorScheme="blue" icon={<MdMyLocation />} onClick={onLocate} aria-label="Locate on map" />
              </Tooltip>
            )}
            <Tooltip label="Open in Google Maps" hasArrow>
              <IconButton as="a" href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer" size="xs" variant="ghost" icon={<MdOpenInNew />} aria-label="Open in Google Maps" />
            </Tooltip>
          </HStack>
        )}
      </HStack>
    </VStack>
  );
});
TopologyCard.displayName = 'TopologyCard';

// ───────────────────────────────────────────────────────────────────────────
export const RawPayload = memo(({ payload, onCopy }) => {
  const [open, setOpen] = useState(false);
  const codeBg = useColorModeValue('gray.50', 'blackAlpha.400');
  // stringified only while open (it can be large)
  const text = useMemo(() => (open ? JSON.stringify(payload, null, 2) : ''), [open, payload]);
  return (
    <Box>
      <Button size="xs" variant="ghost" leftIcon={<MdCode />} rightIcon={open ? <MdExpandLess /> : <MdExpandMore />} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        Raw payload
      </Button>
      <Collapse in={open} animateOpacity>
        {open && (
          <Box mt={2} position="relative">
            <IconButton size="xs" variant="ghost" icon={<MdContentCopy />} position="absolute" top={1} right={1} zIndex={1} onClick={() => onCopy(text, 'Payload')} aria-label="Copy raw payload" />
            <Code display="block" whiteSpace="pre" overflowX="auto" maxH="260px" overflowY="auto" p={3} fontSize="10px" bg={codeBg} borderRadius="lg" w="100%">{text}</Code>
          </Box>
        )}
      </Collapse>
    </Box>
  );
});
RawPayload.displayName = 'RawPayload';

